/**
 * Livello dati del sito pubblico (PIANO.md §8 e §10).
 *
 * I progetti non sono piu' scritti a mano qui: arrivano da /data/projects.json,
 * lo snapshot che il backend rigenera a ogni salvataggio nell'admin.
 * La sorgente storica resta in projects.legacy.ts e serve solo alla migrazione.
 *
 * Lo snapshot viene caricato UNA volta prima del primo render (src/index.tsx) e
 * il browser inizia a scaricarlo in parallelo al bundle grazie al preload in
 * public/index.html: nessuna attesa aggiuntiva rispetto ai dati compilati dentro
 * il bundle, e i componenti restano sincroni come prima.
 */

export interface MediaItem {
  id: string;
  src: string;
  type: 'image' | 'video';
  alt?: string;
  w?: number;       // dimensioni note in anticipo: il Masonry non ricalcola nulla
  h?: number;
  poster?: string;   // fotogramma di anteprima, solo per i video
  /**
   * Versione leggera del video, per la griglia. La galleria avvia tutti i
   * video insieme: con i file a piena qualita' un progetto da sette video
   * significa scaricarne oltre cento megabyte in una volta. Quando manca si
   * usa src, che e' il comportamento di prima.
   */
  anteprima?: string;
}

export interface Project {
  id: number;
  slug: string;
  title: string;
  image: string;      // copertina
  info: string;       // copy del bottone +
  singleColumn: boolean;
  sortOrder: number;
  media: MediaItem[];
}

interface Snapshot {
  version: number;
  generatedAt: string;
  projects: Project[];
}

const SNAPSHOT_URL = '/data/projects.json';

let projects: Project[] = [];
let loaded = false;

/**
 * Mappa src -> dimensioni, ricavata dallo snapshot.
 * Sostituisce mediaMetadata.json: e' lo stesso oggetto che usa Masonry, riempito
 * sul posto in modo che l'import resti valido.
 */
export const mediaMetadata: Record<
  string,
  { w: number; h: number; poster?: string; anteprima?: string }
> = {};

const ingest = (data: Snapshot): void => {
  projects = Array.isArray(data.projects) ? data.projects : [];
  for (const key of Object.keys(mediaMetadata)) delete mediaMetadata[key];
  for (const project of projects) {
    for (const item of project.media || []) {
      if (item.w && item.h) {
        const dati = { w: item.w, h: item.h, poster: item.poster, anteprima: item.anteprima };
        mediaMetadata[item.src] = dati;
        // La griglia riproduce l'anteprima leggera e la cerca con il proprio
        // indirizzo: senza questa riga non troverebbe misure ne' poster, e il
        // layout salterebbe mentre i video arrivano.
        if (item.anteprima) mediaMetadata[item.anteprima] = dati;
      }
    }
  }
  loaded = true;
};

/** Carica lo snapshot. Un solo tentativo di recupero, poi si prosegue comunque. */
export const loadProjects = async (): Promise<void> => {
  if (loaded) return;
  for (let attempt = 0; attempt < 2; attempt++) {
    try {
      // credentials: 'omit' per combaciare con il preload crossorigin in index.html
      const res = await fetch(SNAPSHOT_URL, { credentials: 'omit' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      ingest(await res.json());
      return;
    } catch (err) {
      if (attempt === 1) {
        console.error('[dati] snapshot non caricato:', err);
      } else {
        await new Promise(resolve => setTimeout(resolve, 300));
      }
    }
  }
};

/** Sostituisce i dati senza ricaricare la pagina (anteprima admin, link privati). */
export const setProjects = (data: Snapshot): void => ingest(data);

export const getAllProjects = (): Project[] => projects;

export const getProjectById = (id: number): Project | undefined =>
  projects.find(project => project.id === id);
